import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React from 'react';
import {
  AnalyticsProvider,
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  GridColumn,
  Heading,
  List,
  ListItem,
  Notice,
  Section,
  Separator,
  Spinner,
  Text,
  useIsMounted,
} from 'telia-front-react';
import NotFound from '../../notfound/NotFound';
import { useStores } from '../../stores/RootStore';
import { FetchStatus } from '../../util/FetchStatus';
import { AccessibilityIcons } from './AccessibilityIcons';
import { BranchBusinessHours } from './BranchBusinessHours';
import { BranchImages } from './BranchImages';
import { BranchSpecialHours } from './BranchSpecialHours';
import { BranchTransportTexts } from './BranchTransportTexts';
import { branchBackgroundImage, getTranslationFallbackEt } from './BranchUtil';

export interface BranchDetailsProps {
  reservationUrl?: string;
  callbackUrl?: string;
}

export const BranchDetails = observer((props: BranchDetailsProps): React.JSX.Element => {
  const { branchStore, languageStore, routeStore } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const { isMobile } = useIsMobileHook();
  const branchState = branchStore.getBranchState(routeStore.decodedParams.branchId);
  const isMounted = useIsMounted();

  React.useEffect(() => {
    branchStore.fetchBranch(routeStore.decodedParams.branchId);
  }, [routeStore.decodedParams.branchId]);

  if (isMounted && !routeStore.decodedParams.branchId) {
    return <NotFound />;
  }

  if (!isMounted || [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(branchState.status)) {
    return <Spinner />;
  }

  if (branchState.status === FetchStatus.ERROR) {
    return (
      <Container>
        <Notice type={'error'} id={'error'}>
          {' '}
          {translate('branch.loading.error')}
        </Notice>
      </Container>
    );
  }

  return (
    <>
      <AnalyticsProvider group="Hero">
        <Section
          isFull={true}
          pullTop={true}
          pullBottom={true}
          backgroundImage={branchBackgroundImage}
          backgroundColor={'brand-primary-bg-strong'}
          smallScreenImagePosition="above"
          theme="dark"
        >
          <Container>
            <Grid>
              <GridColumn width={['md-6']} align={['middle-xs']}>
                <Text>
                  <List direction="inline" type="unstyled">
                    <ListItem>
                      <AnalyticsTrigger trigger={{ id: `Link - Go back`, callback: 'onClick' }}>
                        <Button
                          text={translate('branchDetails.backButton')}
                          onClick={() => window.history.back()}
                          theme="dark"
                          variant="secondary"
                          icon="#arrow-left"
                          size={isMobile ? 'small' : 'default'}
                        />
                      </AnalyticsTrigger>
                    </ListItem>
                    <ListItem>
                      <Heading element="h1" className="h2" id="page-heading">
                        {branchState.branch.name}
                      </Heading>
                    </ListItem>
                  </List>
                  <List direction="inline" type="unstyled">
                    <ListItem icon="#map-marker">
                      <AnalyticsTrigger trigger={{ id: `Link - Address`, callback: 'onClick' }}>
                        <Button
                          link={true}
                          text={getTranslationFallbackEt(branchState.branch.streetAddress, languageStore.currentLanguage)}
                          url={branchState.branch.googleMapsLink}
                          target={'_blank'}
                          theme="dark"
                        />
                      </AnalyticsTrigger>
                    </ListItem>
                    <ListItem icon="#time-round">
                      <BranchBusinessHours businessHours={branchState.branch.businessHours} />
                    </ListItem>
                  </List>
                  {!!branchState.branch.specialHours?.slice().length && (
                    <Grid>
                      <GridColumn width={['xs']}>
                        <Card background={'employee-bg-subtlest'} padding={'small'}>
                          <CardContent>
                            <BranchSpecialHours specialHours={branchState.branch.specialHours} />
                          </CardContent>
                        </Card>
                      </GridColumn>
                    </Grid>
                  )}
                  <p className="h5">{getTranslationFallbackEt(branchState.branch.locationDescriptionLong, languageStore.currentLanguage)}</p>
                  <Separator spacing="xsmall" color="invisible" />
                  <Grid align={['middle-xs']}>
                    <GridColumn width={['xs']}>
                      <AnalyticsTrigger trigger={{ id: `Link - Reserve time`, callback: 'onClick' }}>
                        <Button
                          url={`${props.reservationUrl || '/kontaktid/esindused/broneerimine'}?branchId=${branchState.branch.branchId}`}
                          text={translate('branchDetails.reserveTime.button')}
                          iconRight="#arrow-right"
                          theme="dark"
                        />
                      </AnalyticsTrigger>
                    </GridColumn>
                    <GridColumn width={['xs']}>
                      <AnalyticsTrigger trigger={{ id: `Link - Request callback`, callback: 'onClick' }}>
                        <Button
                          url={props.callbackUrl || '/kontaktid/tagasihelistamine'}
                          text={translate('branchDetails.callback.button')}
                          iconRight="#arrow-right"
                          variant="secondary"
                          theme="dark"
                        />
                      </AnalyticsTrigger>
                    </GridColumn>
                  </Grid>
                </Text>
              </GridColumn>
            </Grid>
          </Container>
        </Section>
      </AnalyticsProvider>
      <Separator spacing={'large'} color={'invisible'} />
      <AnalyticsProvider group="Accessibility information">
        <Container>
          <AccessibilityIcons accessibilityInformation={branchState.branch.accessibilityInformation} />
          <Separator spacing="large" color="invisible" />
          <Grid>
            <GridColumn width={['lg-5']} noGutter={true}>
              <BranchTransportTexts />
            </GridColumn>
            {!!branchState.branchImages.slice().length && (
              <GridColumn width={['lg-6']} noGutter={true} offset={['lg-1']}>
                <BranchImages images={branchState.branchImages} />
              </GridColumn>
            )}
          </Grid>
        </Container>
      </AnalyticsProvider>
    </>
  );
});
